import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ActivityLog, ActivityLogFilters, UseActivityLogsOptions } from './types';

export function useActivityLogs(options: UseActivityLogsOptions = {}) {
  const { filters = {}, limit = 100, realtime = false } = options;
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('activity_logs')
        .select(`
          *,
          app_users:actor_id(full_name, email, avatar_url)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      // Apply filters
      if (filters.entityType) {
        query = query.eq('entity_type', filters.entityType);
      }
      if (filters.entityId) {
        query = query.eq('entity_id', filters.entityId);
      }
      if (filters.action) {
        query = query.eq('action', filters.action);
      }
      if (filters.actorId) {
        query = query.eq('actor_id', filters.actorId);
      }
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom.toISOString());
      }
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo.toISOString());
      }

      const { data, error: fetchError } = await query;
      
      if (fetchError) throw fetchError;
      
      // Apply search filter client-side
      let filteredData = data || [];
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredData = filteredData.filter(log => 
          log.entity_type?.toLowerCase().includes(searchLower) ||
          log.action?.toLowerCase().includes(searchLower) ||
          log.app_users?.full_name?.toLowerCase().includes(searchLower) ||
          JSON.stringify(log.new_value)?.toLowerCase().includes(searchLower)
        );
      }
      
      setLogs(filteredData as ActivityLog[]);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch logs'));
    } finally {
      setLoading(false);
    }
  }, [
    filters.entityType,
    filters.entityId,
    filters.action,
    filters.actorId,
    filters.dateFrom?.getTime(),
    filters.dateTo?.getTime(),
    filters.search,
    limit
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Realtime subscription
  useEffect(() => {
    if (!realtime) return;

    const channel = supabase
      .channel('activity_logs_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_logs',
        },
        () => {
          // Fetch the new log with user info
          fetchLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [realtime, fetchLogs]);

  return { logs, loading, error, refetch: fetchLogs };
}

// Helper to get entity-specific logs
export function useEntityActivityLogs(entityType: string, entityId: string) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Use refs to track previous values and prevent unnecessary fetches
  const prevEntityType = useRef(entityType);
  const prevEntityId = useRef(entityId);

  const fetchLogs = useCallback(async () => {
    if (!entityType || !entityId) {
      setLogs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('activity_logs')
        .select(`
          *,
          app_users:actor_id(full_name, email, avatar_url)
        `)
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (fetchError) throw fetchError;
      
      setLogs((data || []) as ActivityLog[]);
    } catch (err) {
      console.error('Error fetching entity activity logs:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch logs'));
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    // Only fetch if the values actually changed or on initial mount
    if (entityType !== prevEntityType.current || entityId !== prevEntityId.current || logs.length === 0) {
      prevEntityType.current = entityType;
      prevEntityId.current = entityId;
      fetchLogs();
    }
  }, [entityType, entityId, fetchLogs]);

  return { logs, loading, error, refetch: fetchLogs };
}
